/**
 * Supabase Notification Service
 *
 * Supabase-backed implementation of {@link INotificationService} — the only
 * notification data implementation, exposed via
 * {@link module:shared/services/notificationService}.
 *
 * Scoping: every query is filtered by BOTH the current user's `profile_id`
 * (from the auth session) AND `family_id`, so a user only ever sees their own
 * notifications within their family.
 *
 * Mapping notes (app camelCase ⇄ DB snake_case):
 *  - Notification.timestamp ⇄ notifications.created_at
 *  - Notification.isRead ⇄ notifications.is_read
 *  - Notification.data ⇄ notifications.data (Json)
 *
 * OFFLINE WRITES, AND WHY THE GATE HAS TWO SIDES
 *   "The network is down" arrives here in two shapes and only one of them is
 *   cheap to detect. `navigator.onLine === false` is the synchronous one and is
 *   checked first, before anything touches the wire. The other is a browser
 *   that reports `onLine === true` over a captive portal, a dead uplink or a
 *   DNS failure — the ordinary mobile case — where the write is issued, never
 *   reaches Postgres, and comes back through postgrest-js as an error envelope
 *   rather than as a rejection. `markAsRead`, `deleteNotification` and
 *   `addNotification` therefore classify their own failure with
 *   {@link isTransientWriteFailure} and enqueue on the transport class ONLY. A
 *   refusal (RLS, validation, a constraint violation) is never queued: it would
 *   replay forever and block the FIFO queue for every other table.
 *
 *   This service has a third position for that second shape, and it is one the
 *   album, calendar and todo services share but do not yet close: its writes
 *   resolve a family scope first, and that resolution can itself die on the
 *   wire, ABOVE the write, with no response to classify.
 *   {@link SupabaseNotificationService.resolveWriteScope} is where that is
 *   handled, and it hands the thrown message to the same classifier rather than
 *   inventing a second mechanism.
 *
 *   The offline insert needs a `family_id` and resolves it from
 *   {@link lastKnownFamilyId} — a value pushed to this module, never fetched —
 *   because a known-offline branch that goes to the wire loses the write rather
 *   than queueing it.
 *
 * Known gaps (documented for QA):
 *  - Notification.expired is runtime-only (no column) — never persisted.
 *  - `markAllAsRead` and `clearAll` have no offline path: both are unbounded
 *    filtered writes, which the queue's `matchId`-per-row model cannot express.
 *    They do report their failures, though — see the comment on `markAllAsRead`.
 *
 * @module shared/services/supabaseNotificationService
 */

import { getSupabaseClient } from '@/shared/clients/supabaseClient';
import {
  getCurrentFamilyId,
  getCurrentProfileId,
  subscribeActiveFamilyChange,
} from '@/shared/lib/familyContext';
import {
  enqueueSupabaseMutation,
  isTransientWriteFailure,
} from '@/shared/lib/offlineMutationQueue';
import { sanitizeText } from '@/shared/lib/sanitize';
import type { Json, Tables, TablesInsert } from '@/shared/types/database';
import type {
  CreateNotificationDTO,
  INotificationService,
  Notification,
} from '@/shared/types/notification.types';

type NotificationRow = Tables<'notifications'>;

/**
 * The two ids every ONLINE write filters on, resolved together before the write
 * is issued. Both are also exactly what the RLS policies check for themselves —
 * see the note on {@link SupabaseNotificationService.markAsRead}.
 */
interface WriteScope {
  readonly profileId: string;
  readonly familyId: string;
}

// ============================================
// Family scope, resolved without the network
// ============================================

/**
 * The family scope this session was last known to be in, kept here so an
 * OFFLINE insert never has to ask the network for it.
 *
 * THIS EXISTS BECAUSE THE OFFLINE BRANCH USED TO CALL `getCurrentFamilyId()`.
 * The comment above that call claimed it was free because the resolver
 * "returns the in-session override set by the family store on hydration" —
 * true only on a HIT. On a miss (no override, empty per-user cache)
 * {@link getCurrentFamilyId} falls through to a `profiles.active_family_id`
 * SELECT, i.e. the wire, from inside a branch that is only ever entered when
 * `navigator.onLine === false`. It rejected, `addNotification` threw, and the
 * `enqueueSupabaseMutation` below it never ran — the notification was lost
 * outright, which is precisely the failure the offline queue exists to
 * prevent.
 *
 * Two feeds, both push, neither of them a round trip:
 *  1. {@link subscribeActiveFamilyChange} — the family store calls
 *     `setActiveFamilyOverride` on hydration and on every family switch, and
 *     `clearFamilyContextCache` pushes `null` on sign-out. This is the normal
 *     path: nothing can reach `addNotification` before `FamilyGate` has let
 *     the app render, and the gate renders only once hydration has succeeded.
 *  2. {@link resolveFamilyId} — every ONLINE read and write in this service
 *     already awaits the resolver, so the answer is memoised on the way past.
 *     That is the backstop for the ordering the listener cannot cover: it
 *     hears CHANGES, not the value that was already set before this module
 *     was imported.
 *
 * The `null` pushed on sign-out is load-bearing rather than hygiene: a stale
 * id here would build the next user's notification into the previous user's
 * family. Row Level Security would refuse that row on replay, but it should
 * never be constructed in the first place.
 *
 * Module scope, not instance scope, deliberately — `notificationService` is a
 * singleton, and a second instance (tests, a future consumer) must observe the
 * same scope rather than start blind.
 */
let lastKnownFamilyId: string | null = null;

subscribeActiveFamilyChange((familyId) => {
  lastKnownFamilyId = familyId;
});

/**
 * Resolve the current family id for an ONLINE call, remembering it for the
 * offline path on the way through. Every network path in this service goes
 * through here instead of calling {@link getCurrentFamilyId} directly.
 */
async function resolveFamilyId(): Promise<string> {
  const familyId = await getCurrentFamilyId();
  lastKnownFamilyId = familyId;
  return familyId;
}

// ============================================
// Mappers (row ⇄ app)
// ============================================

export function rowToNotification(row: NotificationRow): Notification {
  return {
    id: row.id,
    type: row.type,
    title: row.title,
    message: row.message,
    timestamp: row.created_at,
    isRead: row.is_read,
    ...(row.data != null && {
      data: row.data as Readonly<Record<string, unknown>>,
    }),
    ...(row.link != null && { link: row.link }),
  };
}

/**
 * `CreateNotificationDTO` → the row this app inserts: the write direction of
 * {@link rowToNotification}.
 *
 * The online insert and the offline enqueue both go through it, so the two can
 * never drift. That is what makes a replay a straight re-issue of the request
 * that could not be sent, rather than a second, separately-written mapping of
 * the same DTO.
 *
 * Sanitisation lives here for the same reason. `title` and `message` are
 * rendered into the notification list, and a queued row is written to
 * IndexedDB and read back by a possibly much later build — sanitising in the
 * one place both paths share means an offline row cannot carry markup that the
 * online row would have had stripped.
 *
 * The server-generated columns (`id`, `created_at`) are deliberately absent:
 * online, Postgres fills them; offline, `addNotification` supplies them itself
 * and says why.
 */
export function notificationToInsert(
  data: CreateNotificationDTO,
  scope: { readonly familyId: string; readonly profileId: string }
): TablesInsert<'notifications'> {
  return {
    family_id: scope.familyId,
    profile_id: scope.profileId,
    type: data.type,
    title: sanitizeText(data.title),
    message: sanitizeText(data.message),
    is_read: false,
    ...(data.data !== undefined && {
      data: data.data as unknown as Json,
    }),
    ...(data.link !== undefined && { link: data.link }),
  };
}

/** True when the browser reports no network connectivity. */
function isOffline(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

// ============================================
// Supabase Notification Service
// ============================================

export class SupabaseNotificationService implements INotificationService {
  private get client() {
    return getSupabaseClient();
  }

  async getNotifications(): Promise<readonly Notification[]> {
    const [profileId, familyId] = await Promise.all([
      getCurrentProfileId(),
      resolveFamilyId(),
    ]);
    const { data, error } = await this.client
      .from('notifications')
      .select('*')
      .eq('family_id', familyId)
      .eq('profile_id', profileId)
      .order('created_at', { ascending: false });
    if (error) {
      throw new Error(error.message);
    }
    return (data ?? []).map(rowToNotification);
  }

  async getUnreadCount(): Promise<number> {
    const [profileId, familyId] = await Promise.all([
      getCurrentProfileId(),
      resolveFamilyId(),
    ]);
    const { count, error } = await this.client
      .from('notifications')
      .select('id', { count: 'exact', head: true })
      .eq('family_id', familyId)
      .eq('profile_id', profileId)
      .eq('is_read', false);
    if (error) {
      throw new Error(error.message);
    }
    return count ?? 0;
  }

  /**
   * Resolve the scope an online write filters on, or report that THE
   * RESOLUTION ITSELF died on the wire.
   *
   * THIS IS A FAILURE POSITION THE OTHER THREE SERVICES SHARE AND DO NOT YET
   * CLOSE, and handling it is why this answers `null` rather than a pair. Every write below resolves its
   * scope before it issues anything, and {@link resolveFamilyId} falls through
   * to a `profiles.active_family_id` SELECT on an override-and-cache miss — so
   * on a dead uplink that `navigator.onLine` still calls "online", the write
   * REJECTS BEFORE IT IS EVER ISSUED. There is no response, therefore no
   * `status`, therefore nothing for {@link isTransientWriteFailure} to classify
   * at the call site — and the user's tap was lost with nothing queued behind
   * it. That is the same silent loss the classifier below exists to close,
   * arriving one line earlier than anyone was looking for it.
   *
   * A resolver that died on the wire is not a different failure from a write
   * that died on the wire, so it deliberately does not get a different
   * mechanism. The thrown message goes to the SAME classifier on its
   * `status === undefined` path, which reads the transport markers out of the
   * message — `fetchActiveFamilyId` interpolates the PostgREST message into its
   * own, so `TypeError: Failed to fetch` survives the wrapping. A `null` here
   * means precisely what `status === 0` means at a write: queue it.
   *
   * Everything else is rethrown, and that conservative direction is the whole
   * point of asking the classifier rather than assuming. 'Няма активна сесия'
   * and 'Потребителят няма активно семейство' carry no transport marker and are
   * classified PERMANENT, which is the only safe answer: queueing a write for a
   * session that does not exist replays it under whoever signs in next, where
   * RLS refuses it and it dead-letters into a destructive toast at some
   * unrelated moment. Rethrowing puts it in front of `notificationStore`, which
   * already has the try/catch to say so now.
   */
  private async resolveWriteScope(): Promise<WriteScope | null> {
    try {
      const [profileId, familyId] = await Promise.all([
        getCurrentProfileId(),
        resolveFamilyId(),
      ]);
      return { profileId, familyId };
    } catch (error) {
      // A non-`Error` throw classifies as PERMANENT via the empty message,
      // which is the direction to fail in: surface it, never queue it.
      if (
        isTransientWriteFailure(
          undefined,
          error instanceof Error ? error : null
        )
      ) {
        return null;
      }
      throw error;
    }
  }

  /**
   * The queued form of {@link markAsRead}.
   *
   * It carries no family scope, and that is not an omission: the replay matches
   * on the primary key alone and RLS re-derives `current_family_id()` and
   * `auth.uid()` from the session that is replaying it. So every path that
   * reaches this — the offline gate, a resolver that died on the wire, a write
   * that died on the wire — queues an entry carrying exactly the same
   * information, no matter how much of the scope it had managed to resolve
   * first.
   */
  private async enqueueReadReceipt(id: string): Promise<void> {
    await enqueueSupabaseMutation({
      table: 'notifications',
      op: 'update',
      payload: { is_read: true },
      matchId: id,
      description: 'Отбелязване на известие като прочетено',
    });
  }

  /** The queued form of {@link deleteNotification}; see {@link enqueueReadReceipt}. */
  private async enqueueDelete(id: string): Promise<void> {
    await enqueueSupabaseMutation({
      table: 'notifications',
      op: 'delete',
      matchId: id,
      description: 'Изтриване на известие',
    });
  }

  /**
   * Mark one notification read.
   *
   * WHY THERE IS NO `.select('id')` HERE, AND WHY THAT IS A STATEMENT ABOUT
   * THIS TABLE RATHER THAN AN OVERSIGHT. A zero-row UPDATE/DELETE is the ONLY
   * signal PostgREST gives for a USING-clause refusal — a row that fails
   * `USING` is not refused with a 403, it is simply not matched, and the write
   * answers as though it had succeeded. On `tasks`, `task_lists`, `albums`,
   * `photos` and `photo_comments` that matters, because every UPDATE and DELETE
   * policy on them is strictly narrower than the family-wide SELECT policy: a
   * member can see rows they may not remove, so "matched nothing" is a real
   * refusal that has to reach the user, and those writes read their rows back
   * to detect it.
   *
   * `notifications` is the one table in this wave where that class is empty.
   * All four policies carry the identical qual
   * `(family_id = current_family_id()) AND (profile_id = auth.uid())`
   * (`notifications_select_recipient`, `_update_recipient`, `_delete_recipient`,
   * and the same expression as `_insert_own`'s WITH CHECK), and the two `.eq`
   * filters below are that same predicate spelled client-side. So the policy
   * cannot subtract a row the filters admitted, a row this session could READ
   * is a row it may UPDATE and DELETE, and the UPDATE's WITH CHECK is
   * unreachable too because `{ is_read: true }` touches neither scoping column.
   *
   * What a zero-row answer would actually mean here is "that row is not there
   * any more" — another device's delete that realtime has already echoed, a
   * second tab, a double tap. Both of these writes are idempotent from where
   * the user is standing: the notification they wanted read or gone is read or
   * gone. Reading the row back in order to throw would invent a destructive
   * 'Грешка при известията' toast for a benign race, which is this wave's bug
   * wearing the opposite face.
   *
   * NOTHING HERE WILL NOTICE IF THAT STOPS BEING TRUE, and that is the reason
   * this paragraph is so long. The argument rests entirely on the four quals
   * being identical, and a migration that narrows either the UPDATE or the
   * DELETE `USING` would break it SILENTLY — the narrowed policy would report
   * zero rows, not a 403, which is the very mechanism this comment opened by
   * describing. The error envelope is destructured, so a genuine 4xx or 5xx
   * still surfaces; a policy that merely stopped matching would not. Anyone
   * editing those policies has to add the `.select('id')` this method
   * deliberately omits.
   */
  async markAsRead(id: string): Promise<void> {
    // Offline: queue the patch for replay; report optimistic success. RLS
    // scopes the write to the owner's rows when the queue replays.
    if (isOffline()) {
      await this.enqueueReadReceipt(id);
      return;
    }
    const scope = await this.resolveWriteScope();
    if (scope === null) {
      await this.enqueueReadReceipt(id);
      return;
    }
    const { error, status } = await this.client
      .from('notifications')
      .update({ is_read: true })
      .eq('family_id', scope.familyId)
      .eq('profile_id', scope.profileId)
      .eq('id', id);
    // The browser said "online" and the write still never landed — queue it
    // rather than losing it. `error === null` never reaches the classifier: a
    // 2xx that matched no row is not a wire failure and must never be queued.
    if (error && isTransientWriteFailure(status, error)) {
      await this.enqueueReadReceipt(id);
      return;
    }
    if (error) {
      throw new Error(error.message);
    }
  }

  /**
   * Mark every unread notification read.
   *
   * NO QUEUE, BUT NO SILENCE EITHER, and the two halves of that are decided
   * separately. There is no offline branch because this is an unbounded
   * filtered write and the queue's `matchId`-per-row model cannot express it;
   * that gap is tracked on its own and is untouched here.
   *
   * The swallowed result was a different bug that merely lived next to it. This
   * method used to `await` the builder and discard the envelope, so an RLS
   * refusal, a 400 and a dead uplink all resolved as success: the store cleared
   * every badge, the database kept every row unread, and the next load brought
   * them all back with nothing having reported anything. So the envelope is
   * destructured and a failure throws — for BOTH classes, deliberately. A
   * transient failure is not classified and retried here because there is
   * nowhere to queue it; "this could not be saved" is the honest answer whether
   * the database refused the write or never saw it, and `notificationStore` is
   * pessimistic on this action, so the throw simply means the optimistic state
   * is never applied and there is nothing to roll back.
   */
  async markAllAsRead(): Promise<void> {
    const [profileId, familyId] = await Promise.all([
      getCurrentProfileId(),
      resolveFamilyId(),
    ]);
    const { error } = await this.client
      .from('notifications')
      .update({ is_read: true })
      .eq('family_id', familyId)
      .eq('profile_id', profileId)
      .eq('is_read', false);
    if (error) {
      throw new Error(error.message);
    }
  }

  /** Delete one notification. See {@link markAsRead} on why nothing is read back. */
  async deleteNotification(id: string): Promise<void> {
    // Offline: queue the delete for replay; report optimistic success. RLS
    // scopes the write to the owner's rows when the queue replays.
    if (isOffline()) {
      await this.enqueueDelete(id);
      return;
    }
    const scope = await this.resolveWriteScope();
    if (scope === null) {
      await this.enqueueDelete(id);
      return;
    }
    const { error, status } = await this.client
      .from('notifications')
      .delete()
      .eq('family_id', scope.familyId)
      .eq('profile_id', scope.profileId)
      .eq('id', id);
    if (error && isTransientWriteFailure(status, error)) {
      await this.enqueueDelete(id);
      return;
    }
    if (error) {
      throw new Error(error.message);
    }
  }

  /** Delete every notification in scope. See {@link markAllAsRead} on the throw. */
  async clearAll(): Promise<void> {
    const [profileId, familyId] = await Promise.all([
      getCurrentProfileId(),
      resolveFamilyId(),
    ]);
    const { error } = await this.client
      .from('notifications')
      .delete()
      .eq('family_id', familyId)
      .eq('profile_id', profileId);
    if (error) {
      throw new Error(error.message);
    }
  }

  /**
   * Persist the insert for replay and return an optimistic notification.
   *
   * NOTHING IN HERE MAY TOUCH THE WIRE. It is reached only when the network is
   * known to be unusable, so a round trip does not fail gracefully — it throws
   * past the enqueue and loses the notification outright:
   *  - `auth.getSession()` reads the LOCAL persisted session (unlike
   *    `getUser()`, which always issues `GET /auth/v1/user`).
   *  - the family scope comes from `lastKnownFamilyId`, which is pushed to
   *    this module. It is deliberately NOT `getCurrentFamilyId()`, which
   *    goes to `profiles` on a cold cache — see the block comment there.
   *
   * THREE CALLERS, ONE BODY, AND THE SCOPE QUESTION THAT RAISES. The
   * `navigator.onLine === false` gate is the original one; the other two are
   * transport failures the browser called "online" — a scope resolution that
   * died on the wire, and a write that did. Those two arrive here having
   * possibly already resolved a `familyId` of their own, so it looks like a
   * choice between that fresh value and the memo below.
   *
   * It is not a choice, because they are the same value. {@link resolveFamilyId}
   * is one of the two FEEDS of `lastKnownFamilyId` — it writes what it resolves
   * on the way past — so reading the memo immediately after a successful
   * resolve reads exactly what that resolve produced. Routing every caller
   * through the memo therefore keeps the single family-scope memo this module
   * is allowed to have, rather than threading a second candidate down here to
   * be reconciled against it. When the resolve is what FAILED, the memo holds
   * what the family store last advertised, which is the offline branch's
   * contract already — including the `null` that makes this refuse loudly.
   *
   * The redundant local `getSession()` on those two paths is the price, and it
   * is the right trade: it reads the same persisted session `getCurrentProfileId`
   * just read, off disk, with no request behind it.
   */
  private async enqueueOfflineInsert(
    data: CreateNotificationDTO
  ): Promise<Notification> {
    const { data: sessionData } = await this.client.auth.getSession();
    const profileId = sessionData.session?.user.id;
    if (!profileId) {
      throw new Error(
        'Няма активна сесия: не може да се определи текущият потребител.'
      );
    }
    const familyId = lastKnownFamilyId;
    if (familyId === null) {
      // No family scope was ever advertised to this session, so there is
      // nothing local to scope the row to — and `notifications.family_id` is
      // NOT NULL with no default, while replay re-issues the stored payload
      // verbatim and fills in nothing. Queueing without it would not save
      // the notification; it would move the loss to the replay, where it
      // dead-letters into a destructive toast whenever the network happens
      // to come back. Failing HERE keeps it in front of the caller, which
      // can still roll back its optimistic entry and say so.
      //
      // Reachable only on a cold offline boot, and such a boot cannot get
      // past `FamilyGate` to a screen that raises notifications at all.
      throw new Error(
        'Няма заредено семейство: известието не може да бъде запазено офлайн.'
      );
    }
    const payload: TablesInsert<'notifications'> = {
      ...notificationToInsert(data, { familyId, profileId }),
      // The client mints the id offline because this row has to exist NOW
      // (the store renders it optimistically) and again LATER (the replay
      // inserts it), and both must be the SAME row. Left to the server, the
      // replayed insert would return a different id, the realtime echo would
      // not match the optimistic entry that `upsertRealtimeNotification`
      // dedups by, and the user would see the notification twice. It also
      // makes a double replay collide with the primary key instead of
      // writing a duplicate.
      id: crypto.randomUUID(),
      created_at: new Date().toISOString(),
    };
    await enqueueSupabaseMutation({
      table: 'notifications',
      op: 'insert',
      payload,
      description: `Създаване на известие „${payload.title}“`,
    });
    // Safe by construction rather than by the type: `TablesInsert` makes
    // `id`, `created_at` and `is_read` optional and the object above sets
    // all three, so every column `rowToNotification` reads is present.
    return rowToNotification(payload as NotificationRow);
  }

  async addNotification(data: CreateNotificationDTO): Promise<Notification> {
    if (isOffline()) {
      return this.enqueueOfflineInsert(data);
    }
    const scope = await this.resolveWriteScope();
    if (scope === null) {
      return this.enqueueOfflineInsert(data);
    }
    const insert = notificationToInsert(data, scope);
    const {
      data: row,
      error,
      status,
    } = await this.client
      .from('notifications')
      .insert(insert)
      .select('*')
      .single();
    if (error || !row) {
      // The browser said "online" and the insert still never landed — queue it
      // rather than losing it. A refusal falls through and throws.
      //
      // `!row` without an `error` cannot occur on this call: `.single()` turns
      // a zero-row result into `PGRST116`, so the classifier is never reached
      // without an envelope to classify, and a queueable answer here is always
      // a real transport failure rather than a filtered write.
      if (isTransientWriteFailure(status, error)) {
        return this.enqueueOfflineInsert(data);
      }
      throw new Error(error?.message ?? 'Неуспешно създаване на известие.');
    }
    return rowToNotification(row);
  }
}
